<script setup lang="ts">
import { computed, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useField } from 'vee-validate';

import StandardInput from '@/components/StandardInput.vue';
import LocalizedSelect from '@/components/base/LocalizedSelect.vue';

import type { Locales } from '@/services/types/api.types';
import { formatMoney, provideEditLocale } from '@/composables/data-display-utils';
import { useFormsStore } from '@/stores/forms.store';
import { Constants } from '@/constants';

// CONST
const { t } = useI18n();

// STORE
const formsStore = useFormsStore();

// PROPS
const props = defineProps<{
  languageEnabled: Locales[];
  stepIndex: number;
}>();

// FIELDS
const { value: dotVotingTypeValue } = useField<number>(`answers.steps[${props.stepIndex}].dot_voting_type`);
const { value: dotVotingMeasurementTypeValue } = useField<number>(
  `answers.steps[${props.stepIndex}].dot_voting_measurement_type`,
);
const { value: defaultCostPerChoiceValue } = useField<number>(
  `answers.steps[${props.stepIndex}].default_cost_per_choice`,
);
const { value: numberOfStepsValue } = useField<number | null>(`answers.steps[${props.stepIndex}].number_of_steps`);
const { value: totalBudgetValue } = useField<number>(`answers.steps[${props.stepIndex}].total_budget`);

// LANGUE
const editLocale = provideEditLocale(toRef(() => props.languageEnabled));

// COMPUTED
const dotVotingMeasurementTypeOptions = computed(() => formsStore.getDotVotingMeasurementTypes());
const dotVotingTypeRadioOptions = computed(() => [
  {
    value: Constants.Form.DotVoting.Type.Fixed,
    label: t('form.step.dotVotingTypeOptions.fixed.label'),
    subtitle: t('form.step.dotVotingTypeOptions.fixed.subtitle'),
  },
  {
    value: Constants.Form.DotVoting.Type.Variable,
    label: t('form.step.dotVotingTypeOptions.variable.label'),
    subtitle: t('form.step.dotVotingTypeOptions.variable.subtitle'),
  },
]);

const isFixed = computed(() => dotVotingTypeValue.value === Constants.Form.DotVoting.Type.Fixed);
const isVariable = computed(() => dotVotingTypeValue.value === Constants.Form.DotVoting.Type.Variable);
const isMoney = computed(() => dotVotingMeasurementTypeValue.value === Constants.Form.DotVoting.MeasurementType.Money);

const budgetLabel = computed(() =>
  dotVotingMeasurementTypeValue.value === Constants.Form.DotVoting.MeasurementType.Money
    ? t('form.step.total_budget.money')
    : t('form.step.total_budget.points'),
);

const numberOfStepsHint = computed(() =>
  dotVotingMeasurementTypeValue.value === Constants.Form.DotVoting.MeasurementType.Money
    ? t('validation.forms.dot-voting.number_of_steps.dollar-cost-per-step', {
        cost: formatMoney(singleStepValue.value),
      })
    : t('validation.forms.dot-voting.number_of_steps.points-cost-per-step', { cost: singleStepValue.value }),
);

const singleStepValue = computed(() => {
  return numberOfStepsValue.value ? defaultCostPerChoiceValue.value / numberOfStepsValue.value : undefined;
});

const unitLabelLong = computed(() => {
  if (isMoney.value) return '';
  if (dotVotingMeasurementTypeValue.value === Constants.Form.DotVoting.MeasurementType.Points)
    return ' ' + t('global.units.points');
  return '';
});

const formattedBudget = computed(() => {
  if (!totalBudgetValue.value) return '';
  return isMoney.value ? formatMoney(totalBudgetValue.value) : `${totalBudgetValue.value}`;
});

const formattedDefaultCost = computed(() => {
  if (!defaultCostPerChoiceValue.value) return '';
  return isMoney.value ? formatMoney(defaultCostPerChoiceValue.value) : `${defaultCostPerChoiceValue.value}`;
});

const formattedMaximumPerChoice = computed(() => {
  if (!defaultCostPerChoiceValue.value) return '';
  return isMoney.value ? formatMoney(defaultCostPerChoiceValue.value) : `${defaultCostPerChoiceValue.value}`;
});

const summaryText = computed(() => {
  const budget = formattedBudget.value;
  if (!budget) return '';

  if (isFixed.value) {
    const cost = formattedDefaultCost.value;
    return t('form.step.dotVoting.summary.fixed', { budget, cost, unit: unitLabelLong.value });
  } else if (isVariable.value) {
    const maximum = formattedMaximumPerChoice.value;
    const text = t('form.step.dotVoting.summary.flexible', { budget, maximum, unit: unitLabelLong.value });
    return text;
  }

  return '';
});

// FUNCTIONS
const getFieldName = (fieldName: string) => `answers.steps[${props.stepIndex}].${fieldName}`;

const onDotVotingTypeChange = (value: number) => {
  numberOfStepsValue.value = value === Constants.Form.DotVoting.Type.Variable ? 5 : null;
};
</script>

<template>
  <div class="dot-voting-config">
    <div class="dot-voting-config__type-group">
      <h2 class="dot-voting-config__type-group-label">{{ t('form.step.dotVotingType') }}</h2>
      <div class="dot-voting-config__type-options">
        <label
          v-for="option in dotVotingTypeRadioOptions"
          :key="option.value"
          class="dot-voting-config__type-option"
          :class="{ 'dot-voting-config__type-option--active': dotVotingTypeValue === option.value }"
        >
          <q-radio v-model="dotVotingTypeValue" :val="option.value" @update:model-value="onDotVotingTypeChange" />
          <div class="dot-voting-config__type-option-content">
            <div class="dot-voting-config__type-option-label">{{ option.label }}</div>
            <div class="dot-voting-config__type-option-subtitle">{{ option.subtitle }}</div>
          </div>
        </label>
      </div>
    </div>
    <div class="dot-voting-config__budget-group">
      <h2 class="dot-voting-config__budget-group-label">{{ t('form.step.budget') }}</h2>
      <div class="row q-gutter-md">
        <div class="col">
          <standard-input
            :name="getFieldName('total_budget')"
            :label="budgetLabel"
            type="number"
            outlined
            :min="1"
            :prefix="isMoney ? '$' : undefined"
          />
        </div>
        <div class="col">
          <localized-select
            :name="getFieldName('dot_voting_measurement_type')"
            :label="t('form.step.dotVotingMeasurementType')"
            :editLocale="editLocale"
            :options="dotVotingMeasurementTypeOptions"
            emit-value
            map-options
            outlined
          />
        </div>
      </div>
    </div>
    <div class="dot-voting-config__allocation-rules-group">
      <h2 class="dot-voting-config__allocation-rules-group-label">{{ t('form.step.allocationRules') }}</h2>
      <standard-input
        :name="getFieldName('default_cost_per_choice')"
        :label="isFixed ? t('form.step.default_cost_per_choice') : t('form.step.maximum_per_choice')"
        type="number"
        outlined
        :min="1"
        :hint="isFixed ? t('form.step.dotVoting.helper.defaultCost') : t('form.step.dotVoting.helper.maximumPerChoice')"
        :prefix="isMoney ? '$' : undefined"
      />
      <standard-input
        v-if="dotVotingTypeValue === Constants.Form.DotVoting.Type.Variable"
        :name="getFieldName('number_of_steps')"
        :label="t('form.step.number_of_steps')"
        :hint="numberOfStepsHint"
        type="number"
        outlined
        :min="3"
        :max="10"
      />
    </div>
    <div v-if="summaryText" class="summary-banner">
      <q-banner class="summary-content bg-blue-50 text-blue-900 border-l-4 border-blue-500">
        <template #avatar>
          <q-icon name="info" color="blue" />
        </template>
        {{ summaryText }}
      </q-banner>
    </div>
  </div>
</template>

<style lang="scss">
.dot-voting-config {
  h2.dot-voting-config__type-group-label,
  h2.dot-voting-config__budget-group-label,
  h2.dot-voting-config__allocation-rules-group-label {
    margin-bottom: 12px !important;
    font-size: 1.25rem !important;
    font-weight: 500 !important;
  }

  &__type-group {
    width: 100%;
    min-width: 0;
    margin-bottom: 16px;
  }

  &__type-options {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    min-width: 0;
  }

  &__type-option {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 8px;
    cursor: pointer;
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  &__type-option--active {
    border-color: $primary;
    background: rgba($primary, 0.05);
  }

  &__type-option-content {
    display: flex;
    flex-direction: column;
    min-width: 0;
    overflow-wrap: anywhere;
  }

  &__type-option-label {
    font-weight: 500;
  }

  &__type-option-subtitle {
    color: rgba(0, 0, 0, 0.6);
    font-size: 12px;
    line-height: 1.3;
  }
}

.summary-banner {
  .summary-content {
    background: linear-gradient(135deg, #e3f2fd 0%, #f5f9ff 100%) !important;
    border-left: 4px solid #1976d2;
    padding: 1rem !important;
    border-radius: 4px;
    font-size: 0.95rem;
    line-height: 1.5;
    color: #0d47a1 !important;

    :deep(.q-banner__content) {
      margin-left: 0.75rem;
    }
  }
}
</style>
